import { UseQueryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useToaster } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';

import { useStore } from 'models/Provider';

import {
  IntegrationSettings,
  createTemplate,
  loadIntegrationsSettings,
  loadUnhandledTemplates,
  unhandledLoad,
  updateCareerImage,
  updateCompany,
  updateCompanySocialData,
} from './requests';

export const useCompanyInfo = () => {
  const { current } = useStore();
  return useQuery({
    queryKey: ['company'],
    queryFn: unhandledLoad,
    onSuccess: (data) => current.update(data),
  });
};

type UseUpdateCompanyProps = {
  successMessage: string;
  errorMessage: string;
};

export const useUpdateCompanyInfo = ({ successMessage, errorMessage }: UseUpdateCompanyProps) => {
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useToaster();
  return useMutation(updateCompany, {
    onSuccess: () => {
      showSuccess(successMessage);
      return queryClient.invalidateQueries(['company']);
    },
    onError: () => showError(errorMessage),
  });
};

export const useEmailTemplate = () => {
  return useQuery({
    queryKey: ['email-templates'],
    queryFn: loadUnhandledTemplates,
  });
};

export const useCreateMessageTemplate = () => {
  const queryClient = useQueryClient();
  const { t } = useScopedTranslate(createTemplateLocale);
  const { showError, showSuccess } = useToaster();
  return useMutation(createTemplate, {
    onSuccess: () => {
      showSuccess(t('created'));
      return queryClient.invalidateQueries(['email-templates']);
    },
    onError: () => showError(t('error')),
  });
};

export const useUpdateCompanySocialData = ({
  successMessage,
  errorMessage,
}: UseUpdateCompanyProps) => {
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useToaster();
  const { t } = useStore();
  return useMutation(updateCompanySocialData, {
    onSuccess: () => {
      showSuccess(successMessage);
      return queryClient.invalidateQueries(['company']);
    },
    onError: () => {
      showError(t(errorMessage));
    },
  });
};

export const useUpdateCareerImage = () => {
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useToaster();
  const { t } = useStore();
  return useMutation(updateCareerImage, {
    onSuccess: () => {
      showSuccess(t('career.update.image.success'));
      return queryClient.invalidateQueries(['company']);
    },
    onError: () => showError(t('career.update.image.error')),
  });
};

export const useIntegrationSettings = (options?: UseQueryOptions<IntegrationSettings>) => {
  return useQuery({
    queryKey: ['companies-integration-settings'],
    queryFn: loadIntegrationsSettings,
    ...options,
  });
};

const createTemplateLocale = {
  en: {
    error: 'Error creating template',
    created: 'Template created',
  },
  is: {
    error: 'Ekki tókst að búa til sniðmát',
    created: 'Sniðmát stofnað',
  },
};
